import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";

import { Collection, Collections } from "./shop.types";
import initialCollections from "./shop.collections";
import { convertCollections } from "./shop.utils";

export interface ShopState {
  collections: Collections;
  brands: any[];
  brandsPreview: any[];
  currentCollection: Collection | null;
  currentBrand: any | null;
  currentFilterWithCount: any;
  currentCollectionItems: any[];
  currentItem: any;
  currentReviewsByProduct: any;
  currentReviewsByUser: any;
  isAllCollectionsFetching: boolean;
  isAllBrandsFetching: boolean;
  isCollectionItemsFetching: boolean;
  isCollectionFetching: boolean;
  isBrandFetching: boolean;
  isFilterWithCountFetching: boolean;
  isItemFetching: boolean;
  isReviewsByProductFetching: boolean;
  isReviewsByUserFetching: boolean;
  errorMessageOnAllCollectionsFetching: string | null;
  errorMessageOnAllBrandsFetching: string | null;
  errorMessageOnCollectionItemsFetching: string | null;
  errorMessageOnCollectionFetching: string | null;
  errorMessageOnBrandFetching: string | null;
  errorMessageOnFilterWithCountFetching: string | null;
  errorMessageOnItemFetching: string | null;
  errorMessageOnReviewsByProductFetching: string | null;
  errorMessageOnReviewsByUserFetching: string | null;
}

const initialState = {
  collections: initialCollections,
  brands: [],
  brandsPreview: [],
  currentCollection: null,
  currentBrand: null,
  currentFilterWithCount: null,
  currentCollectionItems: [],
  currentItem: null,
  currentReviewsByProduct: null,
  currentReviewsByUser: null,
  isAllCollectionsFetching: false,
  isAllBrandsFetching: false,
  isCollectionItemsFetching: false,
  isCollectionFetching: false,
  isBrandFetching: false,
  isFilterWithCountFetching: false,
  isItemFetching: false,
  isReviewsByProductFetching: false,
  isReviewsByUserFetching: false,
  errorMessageOnAllCollectionsFetching: null,
  errorMessageOnAllBrandsFetching: null,
  errorMessageOnCollectionItemsFetching: null,
  errorMessageOnCollectionFetching: null,
  errorMessageOnBrandFetching: null,
  errorMessageOnFilterWithCountFetching: null,
  errorMessageOnItemFetching: null,
  errorMessageOnReviewsByProductFetching: null,
  errorMessageOnReviewsByUserFetching: null,
} as ShopState;

const shopSlice = createSlice({
  name: "shop",
  initialState,
  reducers: {
    // fetch all collections
    fetchAllCollectionsStart: (state) => {
      state.isAllCollectionsFetching = true;
    },
    fetchAllCollectionsSuccess: (state, action) => {
      const collections = action.payload;

      state.collections = {
        ...state.collections,
        ...convertCollections(collections),
      };

      state.isAllCollectionsFetching = false;
    },
    fetchAllCollectionsFailure: (state, action) => {
      state.errorMessageOnAllCollectionsFetching = action.payload;
      state.isAllCollectionsFetching = false;
    },

    // fetch brands
    fetchAllBrandsStart: (state) => {
      state.isAllBrandsFetching = true;
    },
    fetchAllBrandsSuccess: (state, action) => {
      state.brands = action.payload;
      state.isAllBrandsFetching = false;
    },
    fetchAllBrandsFailure: (state, action) => {
      state.errorMessageOnAllBrandsFetching = action.payload;
      state.isAllBrandsFetching = false;
    },

    // fetch items in a collection
    fetchCollectionItemsStart: (state) => {
      state.isCollectionItemsFetching = true;
    },
    fetchCollectionItemsSuccess: (state, action) => {
      state.currentCollectionItems = action.payload;
      state.isCollectionItemsFetching = false;
    },
    fetchCollectionItemsFailure: (state, action) => {
      state.errorMessageOnCollectionItemsFetching = action.payload;
      state.isCollectionItemsFetching = false;
    },

    // fetch items with a brand
    fetchBrandItemsStart: (state) => {
      state.isCollectionItemsFetching = true;
    },
    fetchBrandItemsSuccess: (state, action) => {
      state.currentCollectionItems = action.payload;
      state.isCollectionItemsFetching = false;
    },
    fetchBrandItemsFailure: (state, action) => {
      state.errorMessageOnCollectionItemsFetching = action.payload;
      state.isCollectionItemsFetching = false;
    },

    // fetch a current collection
    fetchCollectionStart: (state) => {
      state.isCollectionFetching = true;
    },
    fetchCollectionSuccess: (state, action) => {
      state.currentCollection = action.payload;
      state.isCollectionFetching = false;
    },
    fetchCollectionFailure: (state, action) => {
      state.errorMessageOnCollectionFetching = action.payload;
      state.isCollectionFetching = false;
    },

    // fetch a current brand
    fetchBrandStart: (state) => {
      state.isBrandFetching = true;
    },
    fetchBrandSuccess: (state, action) => {
      state.currentBrand = action.payload;
      state.isBrandFetching = false;
    },
    fetchBrandFailure: (state, action) => {
      state.errorMessageOnBrandFetching = action.payload;
      state.isBrandFetching = false;
    },

    // fetch current filter with count
    fetchFilterWithCountStart: (state) => {
      state.isFilterWithCountFetching = true;
    },
    fetchFilterWithCountSuccess: (state, action) => {
      state.currentFilterWithCount = action.payload;
      state.isFilterWithCountFetching = false;
    },
    fetchFilterWithCountFailure: (state, action) => {
      state.errorMessageOnFilterWithCountFetching = action.payload;
      state.isFilterWithCountFetching = false;
    },

    // fetch filter for BRAND page
    fetchFilterForBrandStart: (state) => {
      state.isFilterWithCountFetching = true;
    },
    fetchFilterForBrandSuccess: (state, action) => {
      state.currentFilterWithCount = action.payload;
      state.isFilterWithCountFetching = false;
    },
    fetchFilterForBrandFailure: (state, action) => {
      state.errorMessageOnFilterWithCountFetching = action.payload;
      state.isFilterWithCountFetching = false;
    },

    // fetch a current item
    fetchItemStart: (state) => {
      state.isItemFetching = true;
    },
    fetchItemSuccess: (state, action) => {
      state.currentItem = action.payload;
      state.isItemFetching = false;
    },
    fetchItemFailure: (state, action) => {
      state.errorMessageOnItemFetching = action.payload;
      state.isItemFetching = false;
    },

    // fetch current reviews by product
    fetchReviewsByProductStart: (state) => {
      state.isReviewsByProductFetching = true;
    },
    fetchReviewsByProductSuccess: (state, action) => {
      state.currentReviewsByProduct = action.payload;
      state.isReviewsByProductFetching = false;
    },
    fetchReviewsByProductFailure: (state, action) => {
      state.errorMessageOnReviewsByProductFetching = action.payload;
      state.isReviewsByProductFetching = false;
    },

    // fetch current reviews by user
    fetchReviewsByUserStart: (state) => {
      state.isReviewsByUserFetching = true;
    },
    fetchReviewsByUserSuccess: (state, action) => {
      state.currentReviewsByUser = action.payload;
      state.isReviewsByUserFetching = false;
    },
    fetchReviewsByUserFailure: (state, action) => {
      state.errorMessageOnReviewsByUserFetching = action.payload;
      state.isReviewsByUserFetching = false;
    },

    // empty current item
    emptyCurrentItem: (state) => {
      state.currentItem = null;
    },

    // empty current collection
    emptyCurrentCollection: (state) => {
      state.currentCollection = null;
    },

    // empty current brand
    emptyCurrentBrand: (state) => {
      state.currentBrand = null;
    },

    // empty current reviews by product
    emptyCurrentReviewsByProduct: (state) => {
      state.currentReviewsByProduct = [];
    },

    // empty current reviews by user
    emptyCurrentReviewsByUser: (state) => {
      state.currentReviewsByUser = [];
    },
  },
});

export const shopActions = shopSlice.actions;

export default shopSlice.reducer;
